import React, { useState, useCallback } from "react";
import CustomTable from "@/components/common/table/CustomTable";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import EyeIcon from "@styles/all-images/icons/view-icon.svg";
import SendIcon from "@styles/all-images/icons/send.svg";
import RevertIcon from "@styles/all-images/icons/revert.svg";
import dateHandler from "@/composables/dateUtil";
import { toast } from "react-toastify";
import {
  IMTSDocumentApprovalStatus,
  ImTsDocumentStatus,
} from "@/constants/enums/im-ts-document-status.enum";
import StatusChips, {
  StatusChipEnumType,
} from "@/components/common/StatusChips";
import { useNavigate } from "react-router-dom";
import { NO_DATA_AVAILABLE } from "@/constants/constant-values";
import { usePermissionState } from "@/providers/PermissionProvider";
import { DocumentsPermissions } from "@/constants/permission-modules/base-permissions";

interface TermSheetTableT1T2Props {
  tabId: number;
}

const TermSheetTableT1T2: React.FC<TermSheetTableT1T2Props> = ({ tabId }) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { formatDateWithTime } = dateHandler();
  const [forceReset, setForceReset] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const navigate = useNavigate();
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [itemToRevert, setItemToRevert] = useState<any>(null);
  const { isPermitted } = usePermissionState();

  const handleView = useCallback((doc: any) => {
    navigate(`/view-im-ts-document/${doc.id}?type=ts&history=false`);
  }, []);

  const handleSend = useCallback(
    async (doc: any) => {
      try {
        const { authenticatedCall } = actionsHandler("sendTermSheetDocument");

        const response = await dispatch(
          authenticatedCall({ method: "put", customParams: [doc.id] }),
        );
        if (response.ok) {
          setForceRefresh(true);
          toast.success(response.data.message);
        }
        return response;
      } catch (error) {
        console.error("Error sending term sheet document:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1 },
          },
        };
      }
    },
    [dispatch],
  );

  const handleRevert = useCallback((doc: any) => {
    setItemToRevert(doc);
    setShowRevertConfirm(true);
  }, []);

  const confirmRevert = useCallback(async () => {
    try {
      const { authenticatedCall } = actionsHandler("revertTermSheetDocument");

      const response = await dispatch(
        authenticatedCall({
          method: "put",
          payload: { doc_id: itemToRevert.id },
          customParams: [itemToRevert.id],
        }),
      );
      if (response.ok) {
        toast.success(response.data.message);
        setForceRefresh(true);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setShowRevertConfirm(false);
      setItemToRevert(null);
    }
  }, [dispatch, itemToRevert]);

  const columns = [
    {
      header: "Document Name",
      accessor: "document_name",
      sortable: true,
      sortKey: "document_name",
    },
    {
      header: "Uploaded on Date & Time",
      accessor: (row: any) => formatDateWithTime(row.uploaded_on),
      sortable: true,
      sortKey: "uploaded_on",
    },
    {
      header: "Created For (T4)",
      accessor: "created_for",
      sortable: true,
      sortKey: "created_for",
    },
    {
      header: "Sent By",
      accessor: "sent_by",
      sortable: true,
      sortKey: "sent_by",
    },
    {
      header: "Document Status",
      accessor: (row: any) => (
        <StatusChips
          status={row.document_status}
          type={StatusChipEnumType.IMTSDocument}
          showRejectionNote={true}
          rejectionNote={row.note}
        />
      ),
      sortable: true,
      sortKey: "document_status",
    },
    {
      header: "Approval Status",
      accessor: (row: any) =>
        row.document_status === ImTsDocumentStatus.Sent ? (
          <StatusChips
            status={row.approval_status}
            type={StatusChipEnumType.IMTSDocumentApproval}
            showRejectionNote={true}
            rejectionNote={row.note}
          />
        ) : (
          NO_DATA_AVAILABLE
        ),
      sortable: true,
      sortKey: "approval_status",
    },
    {
      header: "Action",
      accessor: (row: any) => (
        <div className="tw-flex tw-items-center">
          <button onClick={() => handleView(row)} title="View">
            <img
              src={EyeIcon}
              alt="View"
              className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
            />
          </button>

          {row.document_status === ImTsDocumentStatus.NotSent &&
            isPermitted(DocumentsPermissions.SEND_TERMS_SHEET_DOCUMENTS) && (
              <button onClick={() => handleSend(row)} title="Send">
                <img
                  src={SendIcon}
                  alt="Send"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}

          {row.document_status === ImTsDocumentStatus.Sent &&
            row.approval_status === IMTSDocumentApprovalStatus.Pending &&
            isPermitted(
              DocumentsPermissions.APPROVE_REJECT_TERMS_SHEET_DOCUMENTS,
            ) && (
              <button onClick={() => handleRevert(row)} title="Revoke">
                <img
                  src={RevertIcon}
                  alt="Revert"
                  className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                />
              </button>
            )}
        </div>
      ),
    },
  ];

  const fetchDocumentsWrapper = useCallback(
    async (params: any) => {
      try {
        const { authenticatedCall } = actionsHandler("termSheetListT1T2");

        const queryParams = new URLSearchParams({
          limit: params.limit || 10,
          page: params.page || 1,
          search: params.search || "",
          ...(params.sortColumn && {
            ordering:
              (params.sortDirection === "desc" ? "-" : "") + params.sortColumn,
          }),
        }).toString();

        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: queryParams,
          }),
        );

        return response;
      } catch (error) {
        console.error("Error fetching term sheet documents:", error);
        return {
          data: {
            data: [],
            meta: { count: 0, page: 1, total_pages: 1 },
          },
        };
      }
    },
    [dispatch],
  );

  return (
    <>
      <CustomTable
        columns={columns}
        fetchData={fetchDocumentsWrapper}
        forceRefresh={forceRefresh}
        setForceRefresh={setForceRefresh}
        forceReset={forceReset}
        setForceReset={setForceReset}
        noDataMessage="No term sheet documents found"
        noSearchDataMessage="No matching term sheet documents found"
      />

      {showRevertConfirm && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-w-[400px]">
            <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b">
              <div></div>
              <h2 className="tw-text-base tw-font-semibold">Confirm Revoke</h2>
              <button onClick={() => setShowRevertConfirm(false)}>
                <svg
                  className="tw-w-5 tw-h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="tw-p-4 tw-text-center tw-flex tw-justify-center tw-gap-2 tw-py-5 tw-px-6">
              <h6 className="tw-text-[#232323] tw-text-base tw-font-semibold tw-gap-1">
                Are you sure you want to revoke this document?
              </h6>
            </div>

            <div className="tw-flex tw-space-x-4 tw-py-5 tw-px-12">
              <button
                onClick={() => setShowRevertConfirm(false)}
                className="tw-flex-1 tw-px-4 tw-py-2 tw-border font-default-primary-color tw-border-[#146C94] tw-rounded-lg tw-w-[50%] tw-text-center"
              >
                Cancel
              </button>
              <button
                onClick={confirmRevert}
                className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-flex tw-items-center tw-gap-2 tw-w-[50%] tw-justify-center tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TermSheetTableT1T2;
